import { useState } from 'react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { ScrollArea } from 'components/ui/scroll-area';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import { AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useLegalDocument } from 'features/legal/api/get-legal-document';
import { useAcceptLegalDocuments } from 'features/legal/api/accept-legal-documents';
import { LegalDocumentType } from 'types/api';

export type LegalDocumentsAcceptanceDialogProps = {
    open: boolean;
    onAccepted: () => void;
};

export const LegalDocumentsAcceptanceDialog = ({
    open,
    onAccepted,
}: LegalDocumentsAcceptanceDialogProps) => {
    const [agreed, setAgreed] = useState(false);

    const {
        data: termsOfService,
        isLoading: termsLoading,
        error: termsError,
    } = useLegalDocument({
        params: { document_type: LegalDocumentType.TERMS_OF_SERVICE },
        queryConfig: { enabled: open },
    });

    const {
        data: privacyPolicy,
        isLoading: privacyLoading,
        error: privacyError,
    } = useLegalDocument({
        params: { document_type: LegalDocumentType.PRIVACY_POLICY },
        queryConfig: { enabled: open },
    });

    const acceptMutation = useAcceptLegalDocuments({
        mutationConfig: {
            onSuccess: () => {
                toast.success('Legal documents accepted successfully');
                onAccepted();
            },
            onError: (error) => {
                toast.error(
                    'Failed to accept legal documents. Please try again.',
                );
                console.error('Failed to accept legal documents:', error);
            },
        },
    });

    const handleAccept = () => {
        if (!termsOfService || !privacyPolicy) {
            toast.error('Documents are still loading. Please wait.');
            return;
        }

        acceptMutation.mutate({
            terms_of_service_version: termsOfService.version,
            privacy_policy_version: privacyPolicy.version,
        });
    };

    const isLoading = termsLoading || privacyLoading;
    const hasError = termsError || privacyError;
    const canAccept =
        agreed && termsOfService && privacyPolicy && !acceptMutation.isPending;

    return (
        <Dialog open={open} modal>
            <DialogContent className="max-h-[90vh] max-w-4xl p-0">
                <DialogHeader className="p-6 pb-4">
                    <DialogTitle className="flex items-center gap-2 text-2xl font-semibold">
                        <FileText className="h-6 w-6" />
                        Legal Documents
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Please review and accept our Terms of Service and
                        Privacy Policy to continue using Atomix AI.
                    </p>
                </DialogHeader>

                <div className="flex-1 overflow-hidden px-6">
                    {isLoading && (
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-3/4" />
                            <Skeleton className="h-4 w-1/4" />
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-3/4" />
                            </div>
                        </div>
                    )}

                    {hasError && (
                        <Card>
                            <CardContent className="flex items-center justify-center py-8">
                                <div className="flex items-center space-x-2 text-muted-foreground">
                                    <AlertCircle className="h-5 w-5" />
                                    <span>
                                        Failed to load legal documents. Please
                                        try again later.
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {!isLoading &&
                        !hasError &&
                        termsOfService &&
                        privacyPolicy && (
                            <ScrollArea className="h-[400px] pr-4">
                                <div className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-xl">
                                                {termsOfService.display_name}
                                            </CardTitle>
                                            <p className="text-xs text-muted-foreground">
                                                Last updated:{' '}
                                                {format(
                                                    new Date(
                                                        termsOfService.published_at,
                                                    ),
                                                    'MMMM d, yyyy',
                                                )}
                                            </p>
                                        </CardHeader>
                                        <Separator />
                                        <CardContent className="pt-4">
                                            <div className="prose prose-sm max-w-none dark:prose-invert">
                                                <ReactMarkdown>
                                                    {termsOfService.text}
                                                </ReactMarkdown>
                                            </div>
                                        </CardContent>
                                    </Card>

                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-xl">
                                                {privacyPolicy.display_name}
                                            </CardTitle>
                                            <p className="text-xs text-muted-foreground">
                                                Last updated:{' '}
                                                {format(
                                                    new Date(
                                                        privacyPolicy.published_at,
                                                    ),
                                                    'MMMM d, yyyy',
                                                )}
                                            </p>
                                        </CardHeader>
                                        <Separator />
                                        <CardContent className="pt-4">
                                            <div className="prose prose-sm max-w-none dark:prose-invert">
                                                <ReactMarkdown>
                                                    {privacyPolicy.text}
                                                </ReactMarkdown>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </ScrollArea>
                        )}
                </div>

                <div className="border-t p-6 pt-4">
                    <div className="mb-4 flex items-center space-x-2">
                        <Checkbox
                            id="legal-agreement"
                            checked={agreed}
                            onCheckedChange={(checked) =>
                                setAgreed(checked === true)
                            }
                            disabled={isLoading || !!hasError}
                        />
                        <label
                            htmlFor="legal-agreement"
                            className="cursor-pointer text-sm leading-relaxed"
                        >
                            I have read and agree to both the Terms of Service
                            and Privacy Policy
                        </label>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            onClick={handleAccept}
                            disabled={!canAccept || acceptMutation.isPending}
                            className="min-w-24"
                            loading={acceptMutation.isPending}
                        >
                            Accept
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
